import { supabase } from '../lib/supabase';

import { flatToNested } from './flatUtils';

const CACHE_PREFIX = 'ggj_i18n_db_v2';
const DEFAULT_NAMESPACE = 'translation';
const PAGE_SIZE = 400;

interface BundleMeta {
  locale: string;
  version: string;
  key_count: number;
}

interface CachedBundle {
  version: string;
  flat: Record<string, unknown>;
  fetchedAt: number;
}

type I18nInstance = {
  language: string;
  addResourceBundle: (lng: string, ns: string, data: object, deep?: boolean, overwrite?: boolean) => void;
  changeLanguage: (lng: string) => Promise<unknown>;
};

export function isDbI18nEnabled(): boolean {
  const flag = import.meta.env.VITE_I18N_DB_ENABLED;
  if (flag === 'false' || flag === '0') return false;
  return true;
}

function cacheKey(locale: string): string {
  return `${CACHE_PREFIX}_${locale}`;
}

function readCache(locale: string): CachedBundle | null {
  try {
    const raw = localStorage.getItem(cacheKey(locale));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBundle;
    if (!parsed || typeof parsed !== 'object' || !parsed.flat) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(locale: string, bundle: CachedBundle): void {
  try {
    localStorage.setItem(cacheKey(locale), JSON.stringify(bundle));
  } catch {
    // Quota exceeded — skip cache; bundled JSON still works
  }
}

export function invalidateDbI18nCache(locale?: string): void {
  try {
    if (locale) {
      localStorage.removeItem(cacheKey(locale));
      return;
    }
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(`${CACHE_PREFIX}_`)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}

async function fetchBundleMeta(locale: string): Promise<BundleMeta | null> {
  const { data, error } = await supabase.rpc('get_i18n_bundle_meta', { p_locale: locale });
  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[i18n] get_i18n_bundle_meta failed:', error.message);
    }
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const meta = data as BundleMeta;
  return {
    locale: meta.locale ?? locale,
    version: meta.version ?? '0',
    key_count: Number(meta.key_count ?? 0),
  };
}

async function fetchBundleFlatFull(locale: string): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase.rpc('get_i18n_bundle', { p_locale: locale });
  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[i18n] get_i18n_bundle failed:', error.message);
    }
    return null;
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data as Record<string, unknown>;
}

async function fetchBundleFlatPaginated(locale: string): Promise<Record<string, unknown> | null> {
  const merged: Record<string, unknown> = {};
  let offset = 0;
  let pageRpcAvailable = true;

  for (;;) {
    const { data, error } = await supabase.rpc('get_i18n_bundle_page', {
      p_locale: locale,
      p_offset: offset,
      p_limit: PAGE_SIZE,
    });

    if (error) {
      if (import.meta.env.DEV) {
        console.warn('[i18n] get_i18n_bundle_page failed:', error.message);
      }
      if (offset === 0) {
        pageRpcAvailable = false;
        return fetchBundleFlatFull(locale);
      }
      return Object.keys(merged).length > 0 ? merged : null;
    }

    if (!pageRpcAvailable) break;

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      break;
    }

    const page = data as Record<string, unknown>;
    const keys = Object.keys(page);
    if (keys.length === 0) break;

    Object.assign(merged, page);
    offset += keys.length;

    if (keys.length < PAGE_SIZE) break;
  }

  return Object.keys(merged).length > 0 ? merged : null;
}

async function applyBundleToI18n(
  normalized: string,
  flat: Record<string, unknown>,
  i18n: I18nInstance,
): Promise<void> {
  const nested = flatToNested(flat);
  // Local JSON translations win; DB bundle only adds keys missing from bundled locales.
  i18n.addResourceBundle(normalized, DEFAULT_NAMESPACE, nested, true, false);
  if (i18n.language.split('-')[0]?.toLowerCase() === normalized) {
    await i18n.changeLanguage(i18n.language);
  }
}

export interface LoadRemoteBundleResult {
  applied: boolean;
  source: 'network' | 'cache' | 'none';
  keyCount: number;
}

/** Fetch locale bundle from DB (paginated + cached) and merge into i18next. */
export async function loadRemoteLocaleBundle(
  locale: string,
  i18n: I18nInstance,
): Promise<LoadRemoteBundleResult> {
  if (!isDbI18nEnabled()) {
    return { applied: false, source: 'none', keyCount: 0 };
  }

  const normalized = locale.split('-')[0]?.toLowerCase() || 'en';
  const cached = readCache(normalized);
  const meta = await fetchBundleMeta(normalized);

  if (!meta || meta.key_count === 0) {
    return { applied: false, source: 'none', keyCount: 0 };
  }

  if (cached && cached.version === meta.version && Object.keys(cached.flat).length >= meta.key_count) {
    await applyBundleToI18n(normalized, cached.flat, i18n);
    return { applied: true, source: 'cache', keyCount: Object.keys(cached.flat).length };
  }

  const flat = await fetchBundleFlatPaginated(normalized);
  if (!flat || Object.keys(flat).length === 0) {
    if (cached && Object.keys(cached.flat).length > 0) {
      await applyBundleToI18n(normalized, cached.flat, i18n);
      return { applied: true, source: 'cache', keyCount: Object.keys(cached.flat).length };
    }
    return { applied: false, source: 'none', keyCount: 0 };
  }

  writeCache(normalized, {
    version: meta.version,
    flat,
    fetchedAt: Date.now(),
  });

  await applyBundleToI18n(normalized, flat, i18n);
  return { applied: true, source: 'network', keyCount: Object.keys(flat).length };
}

export async function prefetchRemoteLocaleBundles(
  locales: readonly string[],
  i18n: I18nInstance,
): Promise<void> {
  const active = i18n.language.split('-')[0]?.toLowerCase() || 'en';
  const rest = locales.filter((code) => code !== active);
  for (const code of rest) {
    await loadRemoteLocaleBundle(code, i18n);
  }
}
